import React, { useEffect, useRef, useState } from 'react'
import { FaCheckCircle, FaAngleRight } from 'react-icons/fa';
import { MdDiscount, MdKeyboardDoubleArrowRight } from 'react-icons/md';
import { ShieldCheck } from 'lucide-react';
import { BookingCardHeader } from '@/commons/service-hub/BookingCardHeader';

// Shared building blocks for the hero booking cards (AirconBooking,
// AirconGasBooking, AirconCondenserBooking, CassetteAirconBooking).
// Every card is the same shape: title -> trust signals -> service tabs ->
// price tiles -> discount / Service Details / highlights / UEN. Only the
// tiles, tabs and booking criteria differ per service, so those stay in
// each booking component and everything visual lives here.

// Accent colours per theme. Class names must stay as full literal strings
// so Tailwind can see them at build time - do not build them dynamically.
const THEMES = {
    orange: {
        gradient: 'from-orange-600 to-amber-500',
        shadow: 'shadow-orange-500/30',
        tabText: 'text-orange-600',
        arrowText: 'text-orange-500',
        detailsButton: 'text-orange-600 sm:text-white hover:bg-orange-50 hover:text-orange-600',
    },
    blue: {
        gradient: 'from-blue-600 to-cyan-500',
        shadow: 'shadow-blue-500/30',
        tabText: 'text-blue-600',
        arrowText: 'text-blue-500',
        detailsButton: 'text-blue-600 sm:text-white hover:bg-blue-50 hover:text-blue-600',
    },
    purple: {
        gradient: 'from-purple-600 to-pink-500',
        shadow: 'shadow-purple-500/30',
        tabText: 'text-purple-600',
        arrowText: 'text-purple-500',
        detailsButton: 'text-purple-600 sm:text-white hover:bg-purple-50 hover:text-purple-600',
    },
}

// Keyed by the AirconScope serviceType keys, so the same value drives both
// the card colours and the Service Details sheet. Within one card the tabs
// must map to different themes, otherwise switching tabs looks like a no-op.
const SERVICE_THEMES = {
    general: THEMES.orange,
    generalCeiling: THEMES.orange,
    chemicalWash: THEMES.blue,
    chemicalHydroWash: THEMES.blue,
    chemicalOverhaul: THEMES.purple,
    gasRefill: THEMES.orange,
    gasRefillHalf: THEMES.blue,
    compressorCleaning: THEMES.orange,
    compressorChemicalCleaning: THEMES.blue,
}

export const getServiceTheme = (serviceType) => SERVICE_THEMES[serviceType] || THEMES.orange

/**
 * Outer card: title, optional subtitle and the crawlable trust signals.
 * `headingAs` lets a page that already owns its <h1> demote the card title.
 */
// `pills` / `showRating` exist for the UAE / Malaysia heroes
// (components/market/MarketAirconHero); Singapore uses the defaults.
export const BookingCard = ({ title, subtitle, headingAs: Heading = 'h1', pills = ['No hidden fees'], showRating = true, children }) => (
    <div className='pt-6 pb-4 relative max-w-md sm:max-w-2xl mx-auto rounded-xl bg-white sm:bg-black/50 sm:text-white'>
        {/* Trust pills are deliberately limited to claims not already shown lower
            in the card (see BookingFooter's discount badge and highlights). */}
        <BookingCardHeader title={title} subtitle={subtitle} headingAs={Heading} pills={pills} showRating={showRating} />
        {children}
    </div>
)

/**
 * Service type selector. `options` is [{ value, label: [line1, line2], ariaLabel }].
 * `children` are appended after the tabs (e.g. the guide "?" button).
 */
export const ServiceTypeTabs = ({ options, value, onChange, children }) => (
    <div className='text-sm flex justify-center space-x-2 md:space-x-4 pt-5'>
        {options.map(option =>
            <button
                key={option.value}
                aria-label={option.ariaLabel}
                className={(value === option.value
                    ? ` bg-white ring ring-amber-200 ${getServiceTheme(option.value).tabText}`
                    : ' bg-white border text-gray-500 border-gray-200  bg-linear-to-tr from-gray-200 to-white') + ' py-2 rounded-xl w-24'}
                type='button'
                onClick={() => onChange(option.value)}
            >
                {option.label[0]} <br /> {option.label[1]}
            </button>
        )}
        {children}
    </div>
)

// Mobile tile sizing. "scroll" is for rows that overflow (5 tiles) and keeps
// roughly three tiles in view; "fit" is for rows of 3 or fewer, where the
// tiles share the full width so longer labels ("2 Compressors") still fit.
const MOBILE_TILE_SIZE = {
    // px-2, not p-3: "2 AC units" needs the extra width to stay on one line.
    scroll: 'py-3 px-2 shrink-0 w-[calc(33%-8px)]',
    fit: 'py-3 px-2 flex-1 min-w-0',
}

/**
 * A single bookable price tile. `variant` only changes labels/spacing:
 *   desktop -> tile.label      + "SGD 57.41"
 *   mobile  -> tile.shortLabel + "$57.41"
 * tile.priceLabel, when set, replaces both price strings (the UAE / Malaysia
 * tiles pass "AED 119.60" / "RM89.24"). With tile.priceParts ({ currency,
 * amount }) the phone tile puts the currency on a small line of its own and
 * the amount at full size - "AED 1,188.64" does not fit a phone tile's width.
 */
const PriceTile = ({ tile, variant, theme, mobileSize = 'scroll' }) => {
    const isDesktop = variant === 'desktop'
    return (
        <button
            type='button'
            role='button'
            aria-label={tile.ariaLabel}
            className={isDesktop
                ? `relative p-3 overflow-hidden bg-linear-to-br ${theme.gradient} text-white rounded-2xl shadow-lg ${theme.shadow} w-40 text-center`
                : `relative overflow-hidden bg-linear-to-tr ${theme.gradient} text-white rounded-2xl shadow-lg ${theme.shadow} text-center ${MOBILE_TILE_SIZE[mobileSize]}`}
            onClick={tile.onBook}
        >
            <div className="relative">
                <div className={!isDesktop && mobileSize === 'fit' ? 'text-sm leading-tight' : undefined}>
                    {isDesktop ? tile.label : (tile.shortLabel || tile.label)}
                </div>
                {!isDesktop && tile.priceParts ? (
                    <>
                        <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80 mt-1 leading-none">{tile.priceParts.currency}</div>
                        <div className="text-lg font-semibold whitespace-nowrap leading-tight">{tile.priceParts.amount}</div>
                    </>
                ) : (
                /* priceLabel tiles (UAE / Malaysia, "AED 143.52") are longer than
                    "$57.41", so on phones they drop a size and never wrap. */
                <div className={isDesktop
                    ? 'text-lg font-semibold mt-3 inline-block'
                    : (tile.priceLabel ? 'text-base font-semibold mt-1 whitespace-nowrap' : 'text-lg font-semibold mt-1')}>
                    {tile.priceLabel ?? (isDesktop ? `${tile.currencyCd} ${tile.price}` : `$${tile.price}`)}
                </div>
                )}
            </div>
        </button>
    )
}

/**
 * Price tiles plus the "More Options" entry point, for both breakpoints.
 * `tiles` is [{ key, label, shortLabel?, currencyCd, price, ariaLabel, onBook }].
 * Desktop uses 3 columns for 5 tiles + More, and a centred 2x2 when there
 * are only 3 tiles, so a short list does not leave a lone tile on row two.
 * On mobile, 3 or fewer tiles fit without scrolling, so the row is centred,
 * the tiles widen to share the width and the scroll arrow is dropped.
 */
export const PriceTileGrid = ({ tiles, theme, onMore, moreAriaLabel = "Show more aircon servicing options" }) => {
    const scrollRef = useRef(null)
    const [ showArrow, setShowArrow ] = useState(true)
    const fitsOnMobile = tiles.length <= 3

    const scrollToNext = () => {
        const scrollContainer = scrollRef.current
        if (!scrollContainer) return

        scrollContainer.scrollBy({
            left: 150,
            behavior: 'smooth'
        })
    }

    // Hide the mobile arrow once the row is scrolled to the end (or fits).
    useEffect(() => {
        const container = scrollRef.current
        if (!container) return

        const handleScroll = () => {
            const { scrollLeft, scrollWidth, clientWidth } = container
            setShowArrow(scrollLeft + clientWidth < scrollWidth - 10)
        }

        container.addEventListener('scroll', handleScroll)
        handleScroll()

        return () => container.removeEventListener('scroll', handleScroll)
    }, [tiles.length])

    const desktopGrid = tiles.length + 1 > 4
        ? 'hidden sm:grid sm:grid-cols-3 gap-4 pt-4'
        : 'hidden sm:grid sm:grid-cols-2 gap-4 pt-4 max-w-sm mx-auto justify-items-center'

    return (
        <>
            {/* Desktop - Grid */}
            <div className={desktopGrid}>
                {tiles.map(tile => (
                    <PriceTile key={tile.key} tile={tile} variant='desktop' theme={theme} />
                ))}

                {/* More Options Card */}
                <button
                    type='button'
                    aria-label={moreAriaLabel}
                    className='relative p-3 overflow-hidden  bg-linear-to-br from-gray-700 to-gray-900 text-white rounded-2xl shadow-lg w-40 text-center'
                    onClick={onMore}
                >
                    <div className="text-lg font-semibold">More</div>
                    <div className="text-xs opacity-70">Options</div>
                </button>
            </div>

            {/* Mobile - Horizontal Scroll */}
            <div className='sm:hidden relative pt-2'>
                <div className='relative'>
                    <div
                        ref={scrollRef}
                        className={fitsOnMobile
                            ? 'flex justify-center gap-2 pb-2'
                            : 'flex gap-3 overflow-x-auto scrollbar-hide pb-2 pr-12'}
                    >
                        {tiles.map(tile => (
                            <PriceTile
                                key={`mobile-${tile.key}`}
                                tile={tile}
                                variant='mobile'
                                theme={theme}
                                mobileSize={fitsOnMobile ? 'fit' : 'scroll'}
                            />
                        ))}
                    </div>

                    {/* Scroll Indicator Button */}
                    {!fitsOnMobile && showArrow && (
                        <button
                            type="button"
                            onClick={scrollToNext}
                            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 bg-white/95 opacity-80 backdrop-blur-sm rounded-full shadow-lg border border-gray-200"
                            aria-label="Scroll to see more aircon unit options"
                        >
                            <FaAngleRight className={`text-lg ${theme.arrowText}`} />
                        </button>
                    )}
                </div>
                <div className='flex justify-center py-2'>
                    <button
                        type='button'
                        aria-label={moreAriaLabel}
                        className='relative flex items-center text-sm py-3 px-4 rounded-xl  bg-linear-to-br from-gray-600 to-gray-800 text-white text-center space-y-5 overflow-hidden'
                        onClick={onMore}
                    >
                        <span>More Options</span>
                    </button>
                </div>
            </div>
        </>
    )
}

/**
 * Discount badge, Service Details link, highlight pills and company badge.
 * `highlights` is the list of green pills (warranty etc.) - pass only claims
 * that are true for that service.
 */
// `discountText` and `showCompanyBadge` exist for the UAE / Malaysia heroes:
// the "20% off" claim and the UEN badge describe the Singapore business.
export const BookingFooter = ({ theme, serviceType, onShowDetails, highlights, discountText = 'Upto 20% off on first booking!', showCompanyBadge = true }) => (
    <>
        {/* Discount Badge */}
        <div className='flex justify-center items-center gap-2 px-2 pt-2 sm:pt-5 pb-2 sm:pb-5'>
            <span className={` bg-linear-to-r ${theme.gradient} text-white text-lg p-2 rounded-full`}>
                <MdDiscount />
            </span>
            <span className='text-sm font-medium text-gray-700 sm:text-white'>{discountText}</span>
        </div>
        {/* Service Details Button */}
        {onShowDetails &&
            <div className='text-center'>
                <button
                    type="button"
                    aria-label={`View ${serviceType} service details`}
                    className={`inline-flex items-center gap-1 text-sm font-medium px-4 py-2 rounded-full transition-all ${theme.detailsButton}`}
                    onClick={onShowDetails}
                >
                    <MdKeyboardDoubleArrowRight className='text-xl animate-pulse' />
                    <span>Service Details</span>
                </button>
            </div>
        }
        {/* Highlights */}
        <div className='flex justify-center items-center w-full text-sm px-2 pt-5'>
            <div className='flex flex-wrap justify-center gap-4'>
                {highlights.map((item, i) =>
                    <div key={i} className='flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-full'>
                        <span className='text-green-500 text-sm'>
                            <FaCheckCircle />
                        </span>
                        <span className='text-gray-700 text-xs font-medium'>{item}</span>
                    </div>
                )}
            </div>
        </div>
        {/* Company Trust Badge */}
        {showCompanyBadge &&
        <div className="pt-5">
            <div className="flex items-center justify-center gap-1 text-gray-700">
                <ShieldCheck className="w-4 h-4 text-green-400" />
                <p className="text-xs text-gray-400 sm:text-white/70">
                    StringsSG Pte Ltd &middot; UEN 201813375G
                </p>
            </div>
        </div>
        }
    </>
)
